import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, LogOut, User, Settings, BarChart3, Sun, Moon } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';
import { Button, Avatar, Logo } from '@/components/ui';

interface HeaderProps {
  onToggleSidebar?: () => void;
}

const Header = ({ onToggleSidebar }: HeaderProps) => {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-40 glass border-b border-gray-100 dark:border-dark-700">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <Logo size="sm" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {!user ? (
              <>
                <a
                  href="#features"
                  className="text-gray-600 hover:text-dark-800 dark:text-gray-300 dark:hover:text-white transition-colors"
                >
                  {t('nav.features')}
                </a>
                <a
                  href="#how-it-works"
                  className="text-gray-600 hover:text-dark-800 dark:text-gray-300 dark:hover:text-white transition-colors"
                >
                  {t('nav.howItWorks')}
                </a>
                <button
                  onClick={toggleTheme}
                  className="p-2 text-gray-600 hover:text-dark-800 dark:text-gray-300 dark:hover:text-white transition-colors"
                  aria-label="Toggle theme"
                >
                  {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </button>
                <Link to="/login">
                  <Button variant="ghost" size="sm">
                    {t('common.signIn')}
                  </Button>
                </Link>
                <Link to="/register">
                  <Button variant="primary" size="sm">
                    {t('common.getStarted')}
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <Link
                  to="/dashboard"
                  className="text-gray-600 hover:text-dark-800 dark:text-gray-300 dark:hover:text-white transition-colors"
                >
                  {t('nav.dashboard')}
                </Link>
                <Link
                  to="/deeplinks"
                  className="text-gray-600 hover:text-dark-800 dark:text-gray-300 dark:hover:text-white transition-colors"
                >
                  {t('nav.deeplinks')}
                </Link>
                <Link
                  to="/profile"
                  className="text-gray-600 hover:text-dark-800 dark:text-gray-300 dark:hover:text-white transition-colors"
                >
                  {t('nav.profile')}
                </Link>

                {/* Theme Toggle */}
                <button
                  onClick={toggleTheme}
                  className="p-2 text-gray-600 hover:text-dark-800 dark:text-gray-300 dark:hover:text-white transition-colors"
                  aria-label="Toggle theme"
                >
                  {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </button>

                {/* User Menu */}
                <div ref={userMenuRef} className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                  >
                    <Avatar
                      src={user.photoURL || undefined}
                      name={user.displayName || user.email || 'User'}
                      size="sm"
                    />
                  </button>

                  {userMenuOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-card border border-gray-100 py-2 overflow-hidden dark:bg-dark-800 dark:border-dark-700">
                      <div className="px-4 py-3 border-b border-gray-100 dark:border-dark-700">
                        <p className="font-medium text-dark-800 dark:text-white truncate">
                          {user.displayName || 'User'}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
                      </div>
                      <Link
                        to="/stats"
                        className="flex items-center gap-3 px-4 py-3 text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-dark-700 transition-colors"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <BarChart3 className="w-4 h-4" />
                        {t('nav.statistics')}
                      </Link>
                      <Link
                        to="/settings"
                        className="flex items-center gap-3 px-4 py-3 text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-dark-700 transition-colors"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <Settings className="w-4 h-4" />
                        {t('nav.settings')}
                      </Link>
                      <Link
                        to="/profile"
                        className="flex items-center gap-3 px-4 py-3 text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-dark-700 transition-colors"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <User className="w-4 h-4" />
                        {t('nav.editProfile')}
                      </Link>
                      <hr className="my-2 border-gray-100 dark:border-dark-700" />
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 w-full px-4 py-3 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        {t('common.signOut')}
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => {
              if (user && onToggleSidebar) {
                onToggleSidebar();
              } else {
                setMobileMenuOpen(!mobileMenuOpen);
              }
            }}
            className="md:hidden p-2 text-gray-600 hover:text-dark-800 dark:text-gray-300 dark:hover:text-white"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-100 dark:border-dark-700">
            {!user ? (
              <div className="flex flex-col gap-4">
                <a
                  href="#features"
                  className="text-gray-600 hover:text-dark-800 dark:text-gray-300 dark:hover:text-white transition-colors py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('nav.features')}
                </a>
                <a
                  href="#how-it-works"
                  className="text-gray-600 hover:text-dark-800 dark:text-gray-300 dark:hover:text-white transition-colors py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('nav.howItWorks')}
                </a>
                <button
                  onClick={toggleTheme}
                  className="flex items-center gap-2 text-gray-600 hover:text-dark-800 dark:text-gray-300 dark:hover:text-white transition-colors py-2"
                >
                  {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                  {isDark ? t('settings.lightMode') : t('settings.darkMode')}
                </button>
                <div className="flex flex-col gap-2 pt-4">
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" size="md" className="w-full">
                      {t('common.signIn')}
                    </Button>
                  </Link>
                  <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="primary" size="md" className="w-full">
                      {t('common.getStarted')}
                    </Button>
                  </Link>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                <Link
                  to="/dashboard"
                  className="text-gray-600 hover:text-dark-800 dark:text-gray-300 dark:hover:text-white transition-colors py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('nav.dashboard')}
                </Link>
                <Link
                  to="/deeplinks"
                  className="text-gray-600 hover:text-dark-800 dark:text-gray-300 dark:hover:text-white transition-colors py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('nav.deeplinks')}
                </Link>
                <Link
                  to="/profile"
                  className="text-gray-600 hover:text-dark-800 dark:text-gray-300 dark:hover:text-white transition-colors py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('nav.profile')}
                </Link>
                <Link
                  to="/stats"
                  className="text-gray-600 hover:text-dark-800 dark:text-gray-300 dark:hover:text-white transition-colors py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('nav.statistics')}
                </Link>
                <hr className="border-gray-100 dark:border-dark-700" />
                <button
                  onClick={handleLogout}
                  className="text-left text-red-600 hover:text-red-700 transition-colors py-2"
                >
                  {t('common.signOut')}
                </button>
              </div>
            )}
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
